import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { projectsAPI, usersAPI } from '../../services/api';
import {
  Search,
  MapPin,
  DollarSign,
  FolderKanban,
  UserPlus,
  Users,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const STATUS_LABELS = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  agent_assigned: { label: 'Agent Assigned', color: 'bg-indigo-100 text-indigo-700' },
  in_progress: { label: 'In Progress', color: 'bg-cyan-100 text-cyan-700' },
  report_submitted: { label: 'Report Submitted', color: 'bg-purple-100 text-purple-700' },
  under_admin_review: { label: 'Under Admin Review', color: 'bg-orange-100 text-orange-700' },
  released_to_donor: { label: 'Released to Donor', color: 'bg-teal-100 text-teal-700' },
  donor_feedback: { label: 'Donor Feedback', color: 'bg-emerald-100 text-emerald-700' },
  revision_required: { label: 'Revision Required', color: 'bg-rose-100 text-rose-700' },
  verification_complete: { label: 'Verification Complete', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' }
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, agentsRes] = await Promise.all([
        projectsAPI.list(),
        usersAPI.listAgents({ status: 'approved' }).catch(() => ({ data: [] }))
      ]);
      setProjects(projectsRes.data || []);
      setAgents(agentsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveProject = async (projectId) => {
    try {
      await projectsAPI.approve(projectId);
      toast.success('Project approved');
      fetchData();
    } catch (error) {
      console.error('Error approving project:', error);
      toast.error('Failed to approve project');
    }
  };

  const handleRejectProject = async (projectId) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    try {
      await projectsAPI.reject(projectId, reason || '');
      toast.success('Project rejected');
      fetchData();
    } catch (error) {
      console.error('Error rejecting project:', error);
      toast.error('Failed to reject project');
    }
  };

  const handleAssignAgent = async (agentId) => {
    if (!selectedProject) return;
    
    try {
      await projectsAPI.assignAgent(selectedProject.project_id, agentId);
      toast.success('Agent assigned successfully');
      setShowAssignDialog(false);
      setSelectedProject(null);
      fetchData();
    } catch (error) {
      console.error('Error assigning agent:', error);
      toast.error('Failed to assign agent');
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = (project.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (project.country || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (project.organization_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="projects-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">Projects</h1>
        <p className="text-slate-500 mt-1">{projects.length} projects total</p>
      </div>

      {/* Filters */}
      <Card className="stat-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-projects-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="status-filter-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending_review">Pending Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="agent_assigned">Agent Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="report_submitted">Report Submitted</SelectItem>
                <SelectItem value="under_admin_review">Under Admin Review</SelectItem>
                <SelectItem value="released_to_donor">Released to Donor</SelectItem>
                <SelectItem value="donor_feedback">Donor Feedback</SelectItem>
                <SelectItem value="revision_required">Revision Required</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card key={project.project_id} className="stat-card hover-lift" data-testid={`project-${project.project_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900 font-['Manrope'] line-clamp-2">
                      {project.title}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">{project.organization_name}</p>
                  </div>
                </div>

                {(project.country || project.region) && (
                  <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    {project.country}{project.region && `, ${project.region}`}
                  </div>
                )}

                {project.description && (
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{project.description}</p>
                )}

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-500">Progress</span>
                    <span className="font-medium text-slate-700">{project.progress || 0}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${project.progress || 0}%` }}></div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-sm mb-4">
                  {project.budget && (
                    <div className="flex items-center gap-1 text-slate-500">
                      <DollarSign className="w-4 h-4" />
                      ${project.budget.toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Status and Actions */}
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    {getStatusBadge(project.status)}
                    {project.assigned_agent_name && (
                      <span className="text-xs text-slate-500">
                        Agent: {project.assigned_agent_name}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons based on status */}
                  <div className="flex gap-2">
                    {project.status === 'pending_review' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-red-600 hover:bg-red-50"
                          onClick={() => handleRejectProject(project.project_id)}
                          data-testid={`reject-${project.project_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApproveProject(project.project_id)}
                          data-testid={`approve-${project.project_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </>
                    )}

                    {project.status === 'approved' && (
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setSelectedProject(project);
                          setShowAssignDialog(true);
                        }}
                        data-testid={`assign-${project.project_id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Assign Agent
                      </Button>
                    )}

                    {['agent_assigned', 'in_progress', 'verification_complete', 'completed'].includes(project.status) && (
                      <Button size="sm" variant="outline" className="w-full" asChild>
                        <Link to={`/admin/projects/${project.project_id}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="stat-card">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No projects found</h3>
            <p className="text-slate-500">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria' 
                : 'Projects submitted by donors will appear here'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Assign Agent Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => {
        setShowAssignDialog(open);
        if (!open) setSelectedProject(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Assign Agent to Project</DialogTitle>
            <DialogDescription>
              <span className="block font-medium text-slate-700">{selectedProject?.title}</span>
              <span className="text-sm">{selectedProject?.organization_name}</span>
              {selectedProject?.assigned_agent_name && (
                <span className="block mt-2 text-amber-600">
                  Currently assigned to: {selectedProject.assigned_agent_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-[400px] overflow-y-auto">
            {agents.length > 0 ? (
              agents.map((agent) => (
                <div 
                  key={agent.user_id} 
                  className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => handleAssignAgent(agent.user_id)}
                  data-testid={`select-agent-${agent.user_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{agent.name}</p>
                      <p className="text-sm text-slate-500">{agent.email}</p>
                      {agent.agent_profile?.field_of_expertise && (
                        <p className="text-xs text-emerald-600 mt-1">
                          Expertise: {agent.agent_profile.field_of_expertise}
                        </p>
                      )}
                      {agent.agent_profile?.years_of_experience && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {agent.agent_profile.years_of_experience} years experience
                        </p>
                      )}
                      {agent.country && (
                        <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {agent.country}
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="outline">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Assign
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                <p className="font-medium">No approved agents available</p>
                <p className="text-sm mt-1">Approve agent accounts first from the Users page</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
